class ProductDetails {
    constructor() {
        this.mainImage = document.getElementById('ProductMainImage');
        this.thumbnails = document.querySelectorAll('.product-details__thumbnail');
        this.variantInput = document.getElementById('VariantId');
        this.optionButtons = document.querySelectorAll('.product-details__option-value[data-option]');
        this.quantityInput = document.getElementById('Quantity');
        this.quantityButtons = document.querySelectorAll('.product-details__quantity-btn');
        this.textOverlay = document.getElementById('TextOverlay');
        this.customText = document.getElementById('CustomText');
        this.monogramFirst = document.getElementById('MonogramFirst');
        this.monogramMiddle = document.getElementById('MonogramMiddle');
        this.monogramLast = document.getElementById('MonogramLast');
        this.customTextSection = document.getElementById('CustomTextSection');
        this.monogramSection = document.getElementById('MonogramSection');
        this.textToggleButtons = document.querySelectorAll('.product-details__toggle-btn');
        this.fontButtons = document.querySelectorAll('.product-details__option-value[data-font]');
        this.colorButtons = document.querySelectorAll('.product-details__option-value[data-color]');
        this.patternPreviewButtons = document.querySelectorAll('[data-pattern-preview]');

        // Pattern color gallery elements (server-rendered, JS shows/hides groups)
        this.patternColorGallery = document.getElementById('PatternColorGallery');
        this.selectedPatternColorDisplay = document.getElementById('SelectedPatternColor');

        // Form property inputs
        this.textTypeInput = document.getElementById('TextType');
        this.textPropertyInput = document.getElementById('TextProperty');
        this.fontPropertyInput = document.getElementById('FontProperty');
        this.colorPropertyInput = document.getElementById('ColorProperty');
        this.colorCodePropertyInput = document.getElementById('ColorCodeProperty');
        this.printPropertyInput = document.getElementById('PrintProperty');
        this.patternColorPropertyInput = document.getElementById('PatternColorProperty');

        // Track current text mode and color
        this.currentTextMode = 'custom';
        this.currentColor = '#2E2E2E';

        // Track selected options
        this.selectedOptions = {};

        // Parse product data (variants + option names)
        try {
            const dataEl = document.getElementById('ProductData');
            const product = dataEl ? JSON.parse(dataEl.textContent) : null;
            this.productVariants = product?.variants || [];
            this.productOptionNames = (product?.options || []).map(name => this.toHandle(name));
        } catch (_) {
            this.productVariants = [];
            this.productOptionNames = [];
        }

        this.initializeEventListeners();

        // Seed selectedOptions + hidden inputs from server-rendered active buttons
        this.initializeSelectedOptions();

        // Initialize selected font label on load
        this.initializeSelectedFontLabel();

        // Initialize selected color label on load
        this.initializeSelectedColorLabel();

        // Show gallery for any already-active print button on load
        this.initializePatternColorGallery();
    }

    toHandle(str) {
        if (!str) return '';
        return String(str)
            .toLowerCase()
            .trim()
            .replace(/\s+/g, '-')
            .replace(/[^a-z0-9-]/g, '');
    }

    /**
     * Normalize alt text / option labels for comparison: lowercase, strip any
     * punctuation/separators to single spaces. This makes "Baby Yellow",
     * "baby-yellow" and "baby_yellow" all compare equal.
     */
    normalizeAltText(str) {
        return String(str || '')
            .toLowerCase()
            .replace(/[^a-z0-9]+/g, ' ')
            .replace(/\s+/g, ' ')
            .trim();
    }

    /**
     * True when a normalized alt string matches the given label.
     *  - mode 'all'  (color): phrase match, or every word of the label present.
     *                 So "Baby Yellow" needs both "baby" AND "yellow" in the alt.
     *  - mode 'any'  (pattern): phrase match, or any significant word present.
     *                 So a noisy title like "Coco Trunk" still matches alt "coco baby yellow".
     */
    altTextMatches(normalizedAlt, label, mode = 'all') {
        const phrase = this.normalizeAltText(label);
        if (!phrase) return true;
        if (normalizedAlt.includes(phrase)) return true;

        const tokens = phrase.split(' ').filter(Boolean);
        if (mode === 'any') {
            // Ignore short/generic words to avoid false positives
            const significant = tokens.filter(t => t.length >= 3);
            const pool = significant.length ? significant : tokens;
            return pool.some(token => normalizedAlt.includes(token));
        }
        return tokens.every(token => normalizedAlt.includes(token));
    }

    /**
     * Score how well a normalized alt string matches a pattern label.
     *   3 = exact phrase, 2 = all words present, 1 = any significant word, 0 = none.
     */
    patternScore(normalizedAlt, label) {
        const phrase = this.normalizeAltText(label);
        if (!phrase) return 0;
        if (normalizedAlt.includes(phrase)) return 3;
        const tokens = phrase.split(' ').filter(Boolean);
        if (tokens.length && tokens.every(t => normalizedAlt.includes(t))) return 2;
        const significant = tokens.filter(t => t.length >= 3);
        const pool = significant.length ? significant : tokens;
        if (pool.some(t => normalizedAlt.includes(t))) return 1;
        return 0;
    }

    /**
     * Find the thumbnail button whose alt text best matches the selected color
     * (strict — every word) AND any of the candidate pattern labels (scored).
     * Returns the matched thumbnail element, or null.
     */
    findThumbByAltText(colorLabel, patternLabels) {
        const patterns = (Array.isArray(patternLabels) ? patternLabels : [patternLabels])
            .map(p => this.normalizeAltText(p))
            .filter(Boolean);
        const colorNorm = this.normalizeAltText(colorLabel);
        if (!colorNorm && !patterns.length) return null;

        const thumbs = [...document.querySelectorAll('.product-details__thumbnail')];
        let best = null;
        let bestScore = 0;

        for (const thumb of thumbs) {
            const alt = this.normalizeAltText(thumb.dataset.imageAlt);

            // Color (if selected) must match strictly.
            if (colorNorm && !this.altTextMatches(alt, colorLabel, 'all')) continue;

            // Best pattern score across all candidate pattern labels.
            let score = 0;
            if (!patterns.length) {
                score = 1; // color-only match
            } else {
                for (const p of patterns) {
                    const s = this.patternScore(alt, p);
                    if (s > score) score = s;
                }
            }

            if (score > bestScore) {
                bestScore = score;
                best = thumb;
            }
        }

        // When patterns were provided, require an actual pattern match.
        if (patterns.length && bestScore <= 0) return null;
        return best;
    }

    findImageByAltText(colorLabel, patternLabels) {
        const thumb = this.findThumbByAltText(colorLabel, patternLabels);
        return thumb ? (thumb.dataset.imageUrl || null) : null;
    }

    /**
     * Collect every string that could identify the currently chosen pattern:
     * the selected add-on title, the active add-on card title, and any active
     * non-color variant option value.
     */
    getCurrentPatternLabels() {
        const labels = [];
        const addonTitleInput = document.getElementById('SelectedAddonTitle');
        if (addonTitleInput && addonTitleInput.value) labels.push(addonTitleInput.value);

        const activeAddon = document.querySelector('[data-addon-card].active');
        if (activeAddon && activeAddon.dataset.addonTitle) labels.push(activeAddon.dataset.addonTitle);

        this.productOptionNames
            .filter(n => n !== 'color' && n !== 'colour')
            .forEach(optName => {
                const btn = document.querySelector(
                    `.product-details__option-value[data-option="${optName}"].active`
                );
                if (btn && btn.dataset.valueLabel) labels.push(btn.dataset.valueLabel);
            });

        // De-duplicate
        return [...new Set(labels.filter(Boolean))];
    }

    /**
     * Read the currently active color and pattern, look for a product image
     * whose alt text contains both, and update the main image and the active
     * thumbnail. Returns true if a match was found.
     */
    updateMainImageFromCurrentOptions() {
        const colorBtn =
            document.querySelector('.product-details__option-value[data-option="color"].active') ||
            document.querySelector('.product-details__option-value[data-option="colour"].active');
        const colorLabel = colorBtn ? (colorBtn.dataset.valueLabel || '') : '';

        const patternLabels = this.getCurrentPatternLabels();

        const thumb = this.findThumbByAltText(colorLabel, patternLabels);
        const url = thumb ? (thumb.dataset.imageUrl || null) : null;

        if (window.PRODUCT_IMAGE_DEBUG) {
            console.log('[product image match]', {
                colorLabel,
                patternLabels,
                matched: !!url,
                matchedAlt: thumb ? thumb.dataset.imageAlt : null,
                matchedUrl: url,
                availableAlts: [...document.querySelectorAll('.product-details__thumbnail')]
                    .map(t => t.dataset.imageAlt)
            });
        }

        if (url && this.mainImage) {
            this.mainImage.src = url;
            this.mainImage.alt = thumb.dataset.imageAlt || [patternLabels[0], colorLabel].filter(Boolean).join(' ');
            this.setActiveThumbnail(thumb);
            return true;
        }
        return false;
    }

    /** Mark the given thumbnail active and deactivate all others. */
    setActiveThumbnail(activeThumb) {
        document.querySelectorAll('.product-details__thumbnail').forEach(thumb => {
            thumb.classList.toggle('active', thumb === activeThumb);
        });
    }

    getVariantOptionsMap(variant) {
        const optionsMap = {};
        for (let i = 0; i < this.productOptionNames.length; i++) {
            const optName = this.productOptionNames[i];
            const value = variant[`option${i + 1}`];
            if (optName && value != null) optionsMap[optName] = this.toHandle(value);
        }
        return optionsMap;
    }

    initializeEventListeners() {
        // Thumbnail image switching (no fades to avoid blur)
        this.thumbnails.forEach(thumbnail => {
            thumbnail.addEventListener('click', (e) => this.switchImage(e));
        });

        // Variant selection
        this.optionButtons.forEach(button => {
            button.addEventListener('click', (e) => this.selectVariant(e));
        });

        // Quantity controls
        this.quantityButtons.forEach(button => {
            button.addEventListener('click', (e) => this.changeQuantity(e));
        });

        // Quantity input validation
        this.quantityInput.addEventListener('input', (e) => this.validateQuantity(e));

        // Text type toggle
        this.textToggleButtons.forEach(button => {
            button.addEventListener('click', (e) => this.toggleTextType(e));
        });

        // Custom text overlay
        if (this.customText) {
            this.customText.addEventListener('input', (e) => this.updateTextOverlay(e));
        }

        // Monogram inputs
        if (this.monogramFirst) {
            this.monogramFirst.addEventListener('input', () => this.updateMonogram());
            this.monogramMiddle.addEventListener('input', () => this.updateMonogram());
            this.monogramLast.addEventListener('input', () => this.updateMonogram());
        }

        // Font selection
        this.fontButtons.forEach(button => {
            button.addEventListener('click', (e) => this.selectFont(e));
        });

        // Color selection
        this.colorButtons.forEach(button => {
            button.addEventListener('click', (e) => this.selectColor(e));
        });

        // Pattern preview selection
        this.patternPreviewButtons.forEach(button => {
            button.addEventListener('click', (e) => this.selectPatternPreview(e));
        });

        // Pattern color gallery – delegated so it works on server-rendered buttons
        if (this.patternColorGallery) {
            this.patternColorGallery.addEventListener('click', (e) => {
                const btn = e.target.closest('.product-details__gallery-color-btn');
                if (!btn || btn.classList.contains('is-unavailable')) return;
                const grid = btn.closest('.product-details__gallery-for-print');
                grid.querySelectorAll('.product-details__gallery-color-btn')
                    .forEach(b => b.classList.remove('active'));
                btn.classList.add('active');
                this._applyPatternColorSelection(btn);
                this.addSelectionFeedback(btn);
            });
        }

        // When an addon pattern card is selected, update the main image by alt text
        window.addEventListener('product:addon-pattern-changed', () => {
            document.querySelectorAll(
                '.product-details__option-value[data-option="color"][data-color-hex], ' +
                '.product-details__option-value[data-option="colour"][data-color-hex]'
            ).forEach(btn => this.applyHexSwatch(btn));
            this.updateMainImageFromCurrentOptions();
        });
    }

    initializeSelectedOptions() {
        document.querySelectorAll('.product-details__option-value[data-option].active').forEach(btn => {
            const optionName = btn.dataset.option;
            const optionValue = btn.dataset.value;
            this.selectedOptions[optionName] = optionValue;

            // Sync the selected-value display label
            const display = document.querySelector(`.product-details__selected-value[data-for="${optionName}"]`);
            if (display) display.textContent = btn.dataset.valueLabel || '';

            // Sync the PrintProperty hidden input (any non-color variant option)
            if (optionName !== 'color' && this.printPropertyInput) {
                this.printPropertyInput.value = btn.dataset.valueLabel || optionValue;
            }
        });

        this.syncPatternPreviewActiveState();
    }

    initializeSelectedFontLabel() {
        // Find the active font button or fallback to first
        let activeFontBtn = document.querySelector('.product-details__option-value[data-font].active');
        if (!activeFontBtn) {
            activeFontBtn = document.querySelector('.product-details__option-value[data-font]');
        }
        if (!activeFontBtn) return;

        const fakeEvent = { preventDefault: () => { }, stopPropagation: () => { }, currentTarget: activeFontBtn };
        // Reuse selectFont to set label + font property consistently
        this.selectFont(fakeEvent);
    }

    initializeSelectedColorLabel() {
        let activeColorBtn = document.querySelector('.product-details__option-value[data-color].active');
        if (!activeColorBtn) {
            activeColorBtn = document.querySelector('.product-details__option-value[data-color]');
        }
        if (!activeColorBtn) return;

        const labelEl = document.querySelector('.product-details__selected-color');
        if (!labelEl) return;

        const colorLabel = activeColorBtn.dataset.colorLabel || activeColorBtn.querySelector('.product-details__option-text')?.textContent || '';
        labelEl.textContent = colorLabel;

        // Sync hidden inputs
        if (this.colorPropertyInput) this.colorPropertyInput.value = colorLabel;
        if (this.colorCodePropertyInput) this.colorCodePropertyInput.value = activeColorBtn.dataset.color;
    }

    switchImage(event) {
        const thumbnail = event.currentTarget;
        const imageUrl = thumbnail.dataset.imageUrl;
        const imageAlt = thumbnail.dataset.imageAlt;

        // Update main image (no opacity tween to prevent blur)
        this.mainImage.src = imageUrl;
        this.mainImage.alt = imageAlt;

        // Update active thumbnail
        this.thumbnails.forEach(thumb => thumb.classList.remove('active'));
        thumbnail.classList.add('active');

        // No opacity changes to avoid browser resampling blur
    }

    selectVariant(event) {
        const button = event.currentTarget;
        const optionName = button.dataset.option;
        const optionValue = button.dataset.value;
        const valueLabel = button.dataset.valueLabel;
        const variantId = button.dataset.variantId;

        // Update selected value display
        const selectedValueDisplay = document.querySelector(`.product-details__selected-value[data-for="${optionName}"]`);
        if (selectedValueDisplay) {
            selectedValueDisplay.textContent = valueLabel;
        }

        if (button.disabled) return;

        // Update selected options tracking
        this.selectedOptions[optionName] = optionValue;

        // If color selected, auto-apply the first available print for that color
        const isColorOption = optionName === 'color' || optionName === 'colour';
        if (isColorOption) this.applyFirstPrintForColor(optionValue);

        // Show pattern color gallery OR update Color option swatches when print changes
        if (!isColorOption) {
            if (this.patternColorGallery) {
                this.showPatternColors(valueLabel);
            } else {
                this.updateColorSwatchesForPrint(valueLabel);
            }

            if (this.printPropertyInput) {
                this.printPropertyInput.value = valueLabel || optionValue;
            }
        }

        if (isColorOption && this.patternColorPropertyInput) {
            this.patternColorPropertyInput.value = valueLabel || '';
        }

        // Notify pattern addon picker that the color changed
        if (isColorOption) {
            window.dispatchEvent(new CustomEvent('product:color-changed', {
                detail: { colorHandle: optionValue }
            }));
        }

        // Find the correct variant based on all selected options
        const matchingVariant = this.findMatchingVariant();

        if (matchingVariant) {
            // Update variant ID
            this.variantInput.value = matchingVariant.id;

            // Update active state for option group
            const optionGroup = button.closest('.product-details__option');
            optionGroup.querySelectorAll('.product-details__option-value').forEach(btn => {
                btn.classList.remove('active');
            });
            button.classList.add('active');

            // 1. Try to find image by alt text (color + pattern combination) — highest priority
            const altMatched = this.updateMainImageFromCurrentOptions();

            if (!altMatched) {
                // 2. Fall back to metafield trunk_image on the color swatch button
                if (isColorOption && button.dataset.mainImage && this.mainImage) {
                    this.mainImage.src = button.dataset.mainImage;
                    this.mainImage.alt = valueLabel || '';
                    // Sync thumbnail by URL as well
                    this.thumbnails.forEach(thumb => {
                        if (!thumb.dataset.colorValue) {
                            thumb.classList.toggle('active', thumb.dataset.imageUrl === button.dataset.mainImage);
                        }
                    });
                } else if (!isColorOption || !button.dataset.mainImage) {
                    // 3. Fall back to variant featured_image
                    this.updateVariantImage(matchingVariant.id);
                }
            }

            // Update price and availability
            this.updateVariantInfo(matchingVariant.id);

            // Add selection feedback
            this.addSelectionFeedback(button);

            this.syncPatternPreviewActiveState();
        } else {
            console.warn('No matching variant found for options:', this.selectedOptions);
        }
    }

    selectPatternPreview(event) {
        const button = event.currentTarget;
        if (button.disabled) return;

        this.productOptionNames.forEach(optName => {
            const value = button.getAttribute(`data-option-${optName}`);
            const label = button.getAttribute(`data-label-${optName}`);
            if (!value) return;

            this.selectedOptions[optName] = value;

            const selectedValueDisplay = document.querySelector(
                `.product-details__selected-value[data-for="${optName}"]`
            );
            if (selectedValueDisplay && label) {
                selectedValueDisplay.textContent = label;
            }

            document.querySelectorAll(`.product-details__option-value[data-option="${optName}"]`).forEach(btn => {
                btn.classList.remove('active');
            });
            const matchBtn = Array.from(
                document.querySelectorAll(`.product-details__option-value[data-option="${optName}"]`)
            ).find(btn => btn.dataset.value === value);
            if (matchBtn) {
                matchBtn.classList.add('active');
            }

            if (optName !== 'color' && this.printPropertyInput && label) {
                this.printPropertyInput.value = label;
            }
        });

        const nonColorOpt = this.productOptionNames.find(name => name !== 'color' && name !== 'colour');
        const nonColorLabel = nonColorOpt ? button.getAttribute(`data-label-${nonColorOpt}`) : null;
        if (nonColorLabel) {
            if (this.patternColorGallery) {
                this.showPatternColors(nonColorLabel);
            } else {
                this.updateColorSwatchesForPrint(nonColorLabel);
            }
        }

        const colorLabel =
            button.getAttribute('data-label-color') || button.getAttribute('data-label-colour');
        if (colorLabel && button.dataset.mainImage && this.mainImage) {
            this.mainImage.src = button.dataset.mainImage;
            this.mainImage.alt = colorLabel;
            if (this.patternColorPropertyInput) {
                this.patternColorPropertyInput.value = colorLabel;
            }
        }

        const matchingVariant = this.findMatchingVariant();
        if (matchingVariant) {
            if (this.variantInput) {
                this.variantInput.value = matchingVariant.id;
            }
            this.updateVariantImage(matchingVariant.id);
            this.updateVariantInfo(matchingVariant.id);
        }

        document.querySelectorAll('[data-pattern-preview]').forEach(btn => btn.classList.remove('active'));
        button.classList.add('active');
        this.addSelectionFeedback(button);
    }

    syncPatternPreviewActiveState() {
        const previews = document.querySelectorAll('[data-pattern-preview]');
        if (!previews.length) return;

        previews.forEach(btn => {
            const mappedOptions = this.productOptionNames.filter(
                optName => btn.getAttribute(`data-option-${optName}`)
            );
            if (!mappedOptions.length) {
                btn.classList.remove('active');
                return;
            }

            const matches = mappedOptions.every(
                optName => this.selectedOptions[optName] === btn.getAttribute(`data-option-${optName}`)
            );
            btn.classList.toggle('active', matches);
        });
    }

    applyFirstPrintForColor(colorHandle) {
        const colorName = 'color';
        const printName = this.productOptionNames.find(n => n !== colorName);
        if (!printName || !this.productOptionNames.includes(colorName)) return;

        const firstForColor = this.productVariants.find(v => {
            const opts = this.getVariantOptionsMap(v);
            return opts[colorName] === colorHandle;
        });
        if (!firstForColor) return;

        const targetPrintHandle = this.getVariantOptionsMap(firstForColor)[printName];
        if (!targetPrintHandle) return;
        this.selectedOptions[printName] = targetPrintHandle;

        const printButtons = document.querySelectorAll(
            `.product-details__option-value[data-option="${printName}"]`
        );
        printButtons.forEach(btn => btn.classList.remove('active'));
        const matchBtn = Array.from(printButtons).find(btn => btn.dataset.value === targetPrintHandle);
        if (matchBtn) {
            matchBtn.classList.add('active');
            this.updateColorSwatchesForPrint(matchBtn.dataset.valueLabel);
        }
    }

    findMatchingVariant() {
        // Use server variant data for reliable matching
        const keys = Object.keys(this.selectedOptions);
        if (keys.length === 0) return null;

        for (const v of this.productVariants) {
            const opts = this.getVariantOptionsMap(v);
            const matches = keys.every(k => opts[k] === this.selectedOptions[k]);
            if (matches) return { id: String(v.id) };
        }
        return null;
    }

    selectColor(event) {
        event.preventDefault();
        event.stopPropagation();

        const button = event.currentTarget;
        const color = button.dataset.color;
        const colorLabel = button.dataset.colorLabel || button.querySelector('.product-details__option-text').textContent;

        if (!this.textOverlay) return;

        // Update active state
        this.colorButtons.forEach(btn => btn.classList.remove('active'));
        button.classList.add('active');

        // Store current color
        this.currentColor = color;

        // Update form properties
        if (this.colorPropertyInput) {
            this.colorPropertyInput.value = colorLabel;
        }
        if (this.colorCodePropertyInput) {
            this.colorCodePropertyInput.value = color;
        }

        // Update selected color label display
        const selectedColorDisplay = document.querySelector('.product-details__selected-color');
        if (selectedColorDisplay) {
            selectedColorDisplay.textContent = colorLabel;
        }

        // Apply color to text overlay
        if (this.currentTextMode === 'monogram') {
            // Apply to each monogram character
            const chars = this.textOverlay.querySelectorAll('.monogram-char');
            chars.forEach(char => {
                char.style.color = color;
            });
        } else {
            // Apply to entire text overlay
            this.textOverlay.style.color = color;
        }

        // Add selection feedback
        this.addSelectionFeedback(button);
    }

    initializePatternColorGallery() {
        const activeVariantBtn = document.querySelector(
            '.product-details__variants .product-details__option-value[data-option].active:not([data-option="color"])'
        );
        const fallbackVariantBtn = document.querySelector(
            '.product-details__variants .product-details__option-value[data-option]:not([data-option="color"]):not([disabled])'
        );
        const variantBtn = activeVariantBtn || fallbackVariantBtn;

        if (this.patternColorGallery && variantBtn) {
            if (!activeVariantBtn) variantBtn.classList.add('active');
            this.showPatternColors(variantBtn.dataset.valueLabel);
            // intentionally no early return — fall through to also apply the active color image
        } else if (variantBtn) {
            if (!activeVariantBtn) variantBtn.classList.add('active');
            this.updateColorSwatchesForPrint(variantBtn.dataset.valueLabel);
        }

        // Always apply the active color's image on first load
        const activeColorBtn =
            document.querySelector('.product-details__variants .product-details__option-value[data-option="color"].active') ||
            document.querySelector('.product-details__variants .product-details__option-value[data-option="colour"].active') ||
            document.querySelector('.product-details__variants .product-details__option-value[data-option="color"]:not([disabled])') ||
            document.querySelector('.product-details__variants .product-details__option-value[data-option="colour"]:not([disabled])');

        if (activeColorBtn) {
            if (!activeColorBtn.classList.contains('active')) {
                activeColorBtn.closest('.product-details__option')
                    ?.querySelectorAll('.product-details__option-value')
                    .forEach(b => b.classList.remove('active'));
                activeColorBtn.classList.add('active');
            }
            // Store so DOMContentLoaded can fire the initial color-changed event
            this._initialColorHandle = activeColorBtn.dataset.value || '';
        }

        // Try alt-text match for initial image; fall back to color swatch / variant image
        const altMatched = this.updateMainImageFromCurrentOptions();
        if (!altMatched && activeColorBtn?.dataset.mainImage && this.mainImage) {
            this.mainImage.src = activeColorBtn.dataset.mainImage;
            this.mainImage.alt = activeColorBtn.dataset.valueLabel || '';
        }
    }

    /** Apply a hex color swatch from data-color-hex on a color option button. */
    applyHexSwatch(btn) {
        const hex = btn.dataset.colorHex;
        if (!hex) return false;

        const container = btn.querySelector('.product-details__color-swatch');
        if (!container) return false;

        let swatchEl = container.querySelector('.product-details__swatch-icon');
        if (!swatchEl) {
            swatchEl = document.createElement('span');
            swatchEl.className = 'product-details__swatch-icon';
            container.appendChild(swatchEl);
        }

        if (swatchEl.tagName === 'IMG') {
            const span = document.createElement('span');
            span.className = 'product-details__swatch-icon product-details__swatch-icon--hex';
            span.style.backgroundColor = hex;
            swatchEl.replaceWith(span);
        } else {
            swatchEl.classList.add('product-details__swatch-icon--hex');
            swatchEl.style.backgroundColor = hex;
        }
        return true;
    }

    updateColorSwatchesForPrint(printLabel) {
        const printHandle = this.toHandle(printLabel);
        if (!printHandle) return;

        document.querySelectorAll('.product-details__option-value[data-option="color"]').forEach(btn => {
            if (btn.dataset.colorHex) {
                this.applyHexSwatch(btn);
            }

            const swatchUrl = btn.getAttribute(`data-swatch-${printHandle}`);
            const mainUrl = btn.getAttribute(`data-main-${printHandle}`);
            const swatchEl = btn.querySelector('.product-details__swatch-icon');

            if (!btn.dataset.colorHex && swatchUrl && swatchEl) {
                if (swatchEl.tagName === 'IMG') {
                    swatchEl.src = swatchUrl;
                } else {
                    const img = document.createElement('img');
                    img.src = swatchUrl;
                    img.alt = btn.dataset.valueLabel || '';
                    img.className = 'product-details__swatch-icon';
                    img.loading = 'lazy';
                    swatchEl.replaceWith(img);
                }
            }

            if (mainUrl) {
                btn.dataset.mainImage = mainUrl;
                if (btn.classList.contains('active') && this.mainImage) {
                    this.mainImage.src = mainUrl;
                    this.mainImage.alt = btn.dataset.valueLabel || '';
                    if (this.patternColorPropertyInput) {
                        this.patternColorPropertyInput.value = btn.dataset.valueLabel || '';
                    }
                }
            }
        });
    }

    showPatternColors(printLabel) {
        if (!this.patternColorGallery) return;

        const handle = this.toHandle(printLabel);
        const grids = this.patternColorGallery.querySelectorAll('.product-details__gallery-for-print');
        let found = false;

        grids.forEach(grid => {
            if (grid.dataset.print === handle) {
                grid.style.display = 'flex';
                found = true;
                // Auto-select first button if none is active yet
                if (!grid.querySelector('.product-details__gallery-color-btn.active')) {
                    const first = grid.querySelector('.product-details__gallery-color-btn');
                    if (first) {
                        first.classList.add('active');
                        this._applyPatternColorSelection(first);
                    }
                }
            } else {
                grid.style.display = 'none';
            }
        });

        this.patternColorGallery.style.display = found ? 'block' : 'none';
    }

    _applyPatternColorSelection(button) {
        const imgUrls = (button.dataset.imgUrls || '').split('|').filter(Boolean);
        const fallbackUrl = button.dataset.imgUrl || '';
        const firstUrl = imgUrls.length > 0 ? imgUrls[0] : fallbackUrl;
        const colorName = button.dataset.colorName || '';

        // Swap main product image to the selected color's first image
        if (this.mainImage && firstUrl) {
            this.mainImage.src = firstUrl;
            this.mainImage.alt = colorName;
        }

        // Update selected label display
        if (this.selectedPatternColorDisplay) {
            this.selectedPatternColorDisplay.textContent = colorName;
        }

        // Update cart property
        if (this.patternColorPropertyInput) {
            this.patternColorPropertyInput.value = colorName;
        }
    }

    addSelectionFeedback(button) {
        // Add a subtle animation to show the selection
        button.style.transform = 'scale(1.1)';
        setTimeout(() => {
            button.style.transform = 'scale(1)';
        }, 200);
    }

    updateVariantImage(variantId) {
        // Prefer server-provided image map from hidden JSON
        try {
            if (!this.productVariants) {
                const el = document.getElementById('ProductData');
                this.productVariants = el ? JSON.parse(el.textContent).variants : [];
            }
        } catch (_) {
            this.productVariants = [];
        }

        const variant = (this.productVariants || []).find(v => String(v.id) === String(variantId));
        const src = variant?.featured_image?.src || variant?.image?.src || null;
        const alt = variant?.title || this.mainImage.alt;

        if (src) {
            // Use 1200px width for crisper image; Shopify auto-derives sizes via params
            const finalSrc = src.includes('?') ? `${src}&width=1200` : `${src}?width=1200`;
            this.mainImage.src = finalSrc;
            this.mainImage.alt = alt;
            return;
        }

        // Fallback to current src (no blur animations)
    }

    findVariantImage(variantId, optionValue) {
        // This would typically access Shopify's variant data
        // For now, we'll return null and use the fallback method
        return null;
    }

    findMatchingImage(optionValue) {
        // Look through all product images to find one that matches the variant
        const allImages = document.querySelectorAll('.product-details__thumbnail img');
        const optionValueLower = optionValue.toLowerCase();

        for (const img of allImages) {
            const altText = img.alt.toLowerCase();
            const src = img.src;

            // Check if the image alt text or src contains the variant value
            if (altText.includes(optionValueLower) || src.includes(optionValueLower)) {
                return { src: src, alt: img.alt };
            }
        }

        return null;
    }

    updateVariantInfo(variantId) {
        // This would typically update price, availability, etc.
        // For now, we'll just add a subtle feedback animation
        const addToCartButton = document.querySelector('.product-details__add-to-cart');
        if (addToCartButton) {
            addToCartButton.style.transform = 'scale(1.05)';
            setTimeout(() => {
                addToCartButton.style.transform = 'scale(1)';
            }, 200);
        }
    }

    changeQuantity(event) {
        const action = event.currentTarget.dataset.action;
        const currentValue = parseInt(this.quantityInput.value) || 1;

        if (action === 'increase') {
            this.quantityInput.value = currentValue + 1;
        } else if (action === 'decrease' && currentValue > 1) {
            this.quantityInput.value = currentValue - 1;
        }

        // Add feedback animation
        event.currentTarget.style.transform = 'scale(0.95)';
        setTimeout(() => {
            event.currentTarget.style.transform = 'scale(1)';
        }, 150);
    }

    validateQuantity(event) {
        const value = parseInt(event.target.value);
        if (value < 1) {
            event.target.value = 1;
        }
    }

    updateTextOverlay(event) {
        if (!this.textOverlay) return;

        const text = event.target.value.trim();
        this.textOverlay.textContent = text;
        this.textOverlay.classList.remove('product-details__text-overlay--monogram');

        // Update form property
        if (this.textPropertyInput) {
            this.textPropertyInput.value = text;
        }

        // Add a subtle animation
        this.textOverlay.style.transform = 'translate(-50%, -50%) scale(1.05)';
        setTimeout(() => {
            this.textOverlay.style.transform = 'translate(-50%, -50%) scale(1)';
        }, 150);
    }

    toggleTextType(event) {
        const button = event.currentTarget;
        const textType = button.dataset.textType;

        // Update active state
        this.textToggleButtons.forEach(btn => btn.classList.remove('active'));
        button.classList.add('active');

        // Update text type property
        if (this.textTypeInput) {
            this.textTypeInput.value = textType === 'custom' ? 'Custom Text' : 'Monogram';
        }

        // Show/hide appropriate section
        if (textType === 'custom') {
            this.customTextSection.style.display = 'block';
            this.monogramSection.style.display = 'none';
            this.currentTextMode = 'custom';
            // Clear monogram inputs
            if (this.monogramFirst) {
                this.monogramFirst.value = '';
                this.monogramMiddle.value = '';
                this.monogramLast.value = '';
            }
            // Clear monogram property
            if (this.textPropertyInput) {
                this.textPropertyInput.value = this.customText ? this.customText.value : '';
            }
            // Trigger text update if there's existing text
            if (this.customText && this.customText.value) {
                this.updateTextOverlay({ target: this.customText });
            } else {
                this.textOverlay.textContent = '';
                this.textOverlay.classList.remove('product-details__text-overlay--monogram');
            }
        } else {
            this.customTextSection.style.display = 'none';
            this.monogramSection.style.display = 'block';
            this.currentTextMode = 'monogram';
            // Clear custom text
            if (this.customText) {
                this.customText.value = '';
            }
            // Update monogram if there's existing input
            if (this.monogramFirst && (this.monogramFirst.value || this.monogramMiddle.value || this.monogramLast.value)) {
                this.updateMonogram();
            } else {
                this.textOverlay.textContent = '';
                this.textOverlay.classList.remove('product-details__text-overlay--monogram');
            }
        }
    }

    updateMonogram() {
        if (!this.textOverlay) return;

        const first = this.monogramFirst.value.trim().toUpperCase();
        const middle = this.monogramMiddle.value.trim().toUpperCase();
        const last = this.monogramLast.value.trim().toUpperCase();

        // Create monogram HTML with styled middle character
        this.textOverlay.innerHTML = `
            <span class="monogram-char">${first}</span>
            <span class="monogram-char monogram-char--large">${middle}</span>
            <span class="monogram-char">${last}</span>
        `;
        this.textOverlay.classList.add('product-details__text-overlay--monogram');

        // Update form property with combined monogram
        if (this.textPropertyInput) {
            this.textPropertyInput.value = `${first}${middle}${last}`;
        }

        // Add animation
        this.textOverlay.style.transform = 'translate(-50%, -50%) scale(1.05)';
        setTimeout(() => {
            this.textOverlay.style.transform = 'translate(-50%, -50%) scale(1)';
        }, 150);
    }

    selectFont(event) {
        event.preventDefault();
        event.stopPropagation();

        const button = event.currentTarget;
        const font = button.dataset.font;

        if (!this.textOverlay) return;

        // Update active state
        this.fontButtons.forEach(btn => btn.classList.remove('active'));
        button.classList.add('active');

        let fontFamily;
        let fontLabel;
        // Determine font family
        switch (font) {
            case 'single-romanesque':
                fontFamily = '"SingleRomanesque", serif';
                fontLabel = 'Romanesque';
                break;
            case 'buket-roman':
                fontFamily = '"BuketRoman", serif';
                fontLabel = 'Buket Roman';
                break;
            case 'harman-script':
                fontFamily = '"HarmanScript", cursive';
                fontLabel = 'Harman Script';
                break;
            case 'harman-western':
                fontFamily = '"HarmanWestern", serif';
                fontLabel = 'Harman Western';
                break;
            case 'janda-as-long':
                fontFamily = '"JandaAsLongAsYouLoveMe", cursive';
                fontLabel = 'Janda Script';
                break;
            case 'petunia-script':
                fontFamily = '"PetuniaScript", cursive';
                fontLabel = 'Petunia Script';
                break;
            default:
                fontFamily = '"SingleRomanesque", serif';
                fontLabel = 'Romanesque';
                break;
        }

        // Update form property
        if (this.fontPropertyInput) {
            this.fontPropertyInput.value = fontLabel;
        }

        // Update selected font display
        const selectedFontDisplay = document.querySelector('.product-details__selected-font');
        if (selectedFontDisplay) {
            selectedFontDisplay.textContent = fontLabel;
        }

        // Apply font to text overlay
        if (this.currentTextMode === 'monogram') {
            // Apply to each monogram character
            const chars = this.textOverlay.querySelectorAll('.monogram-char');
            chars.forEach(char => {
                char.style.fontFamily = fontFamily;
            });
        } else {
            // Apply to entire text overlay
            this.textOverlay.style.fontFamily = fontFamily;
        }

        // Add mobile positioning class for script fonts
        this.textOverlay.classList.remove('script-font-mobile');
        if (font === 'harman-script' || font === 'janda-as-long' || font === 'petunia-script') {
            this.textOverlay.classList.add('script-font-mobile');
        }

        // Add selection feedback
        this.addSelectionFeedback(button);
    }
}

// Initialize when DOM is loaded
// Handle card message
const handleCardMessage = () => {
    const cardMessageInput = document.getElementById('CardMessage');
    const cardMessageProperty = document.getElementById('CardMessageProperty');

    if (cardMessageInput && cardMessageProperty) {
        cardMessageInput.addEventListener('input', (e) => {
            cardMessageProperty.value = e.target.value.trim();
        });
    }
};

// ── Pattern add-on logic ────────────────────────────────────────────────────
class PatternAddonPicker {
    constructor() {
        this.addonCards = document.querySelectorAll('[data-addon-card]');
        if (!this.addonCards.length) return;

        this.addonVariantInput = document.getElementById('SelectedAddonVariantId');
        this.addonTitleInput   = document.getElementById('SelectedAddonTitle');
        this.selectedLabelEl   = document.getElementById('SelectedPatternLabel');
        this.productForm       = document.querySelector('.product-details__form form');
        this.variantInput      = document.getElementById('VariantId');
        this.quantityInput     = document.getElementById('Quantity');
        this.buttonsWrapper    = document.querySelector('.product-details__buttons');

        this.selectedAddonVariantId = '';

        this.addonCards.forEach(card => {
            card.addEventListener('click', () => this.selectAddon(card));
        });

        // When the main product's color changes, re-evaluate which trunk_colors image to show
        // Color change is already handled by ProductDetails.selectVariant which
        // calls updateMainImageFromCurrentOptions (reads SelectedAddonTitle for pattern).
        // Nothing extra needed here.

        // Auto-select first available (non-OOS) pattern on load
        const firstActive = [...this.addonCards].find(c => c.classList.contains('active') && !c.disabled)
            || [...this.addonCards].find(c => !c.disabled);
        if (firstActive) this.selectAddon(firstActive);

        if (this.productForm) {
            this.productForm.addEventListener('submit', (e) => this.handleSubmit(e));
        }
    }

    /** Returns the handleized color value currently active on the main product. */
    getCurrentColorHandle() {
        const btn = document.querySelector(
            '.product-details__option-value[data-option="color"].active, ' +
            '.product-details__option-value[data-option="colour"].active'
        );
        return btn ? (btn.dataset.value || '') : '';
    }

    /**
     * Swap the main product image to the trunk_colors entry that matches colorHandle.
     * If no color-matched image exists, the main image is left unchanged.
     */
    updatePatternImageForColor(card, colorHandle) {
        if (!card) return;
        const imgUrl = (colorHandle && card.getAttribute(`data-color-img-${colorHandle}`)) || '';
        if (!imgUrl) return;
        const mainImg = document.getElementById('ProductMainImage');
        if (mainImg) {
            mainImg.src = imgUrl;
            mainImg.alt = card.dataset.addonTitle || '';
        }
    }

    selectAddon(card) {
        if (card.disabled) return;
        this.addonCards.forEach(c => c.classList.remove('active'));
        card.classList.add('active');

        this.selectedAddonVariantId = card.dataset.addonVariantId || '';
        const title = card.dataset.addonTitle || '';
        const price = parseInt(card.dataset.addonPrice || '0', 10);

        if (this.addonVariantInput) this.addonVariantInput.value = this.selectedAddonVariantId;
        if (this.addonTitleInput)   this.addonTitleInput.value   = title;

        // Update selected label
        if (this.selectedLabelEl) {
            this.selectedLabelEl.textContent = title || '';
        }

        // Show/hide add-on notice and hide Buy Now when addon is chosen
        const hasAddon = this.selectedAddonVariantId !== '';
        if (this.buttonsWrapper) {
            this.buttonsWrapper.classList.toggle('has-addon', hasAddon);
        }

        // Filter & update color swatches to show only trunk_colors for this pattern.
        // applyTrunkColorsFilter may auto-click a color (triggering selectVariant →
        // updateMainImageFromCurrentOptions). After it settles, fire the addon event
        // so ProductDetails can find the right image by alt text.
        this.applyTrunkColorsFilter(card);

        window.dispatchEvent(new CustomEvent('product:addon-pattern-changed'));
    }

    /**
     * Reads data-color-img-* attributes from the selected pattern card and
     * shows only those color variant swatches in the main Color option section,
     * updating each swatch image to the trunk_colors image for that pattern.
     * When no trunk_colors are found on the card all swatches are shown as-is.
     */
    applyTrunkColorsFilter(card) {
        const trunkColorHandles = new Set();
        const trunkColorImages  = {};

        for (const attr of card.attributes) {
            const match = attr.name.match(/^data-color-img-(.+)$/);
            if (match) {
                const handle = match[1];
                trunkColorHandles.add(handle);
                trunkColorImages[handle] = attr.value;
            }
        }

        const hasFilter = trunkColorHandles.size > 0;

        const colorBtns = document.querySelectorAll(
            '.product-details__option-value[data-option="color"], ' +
            '.product-details__option-value[data-option="colour"]'
        );

        let activeStillVisible = false;
        let firstVisible       = null;

        colorBtns.forEach(btn => {
            const handle = btn.dataset.value || '';
            const show   = !hasFilter || trunkColorHandles.has(handle);

            btn.style.display = show ? '' : 'none';

            if (show) {
                if (!firstVisible) firstVisible = btn;
                if (btn.classList.contains('active')) activeStillVisible = true;

                if (hasFilter && trunkColorImages[handle]) {
                    if (btn.dataset.colorHex) {
                        window.__productDetails?.applyHexSwatch?.(btn);
                    } else {
                        const fullUrl  = trunkColorImages[handle];
                        const swatchUrl = fullUrl.replace(/([?&]width=)\d+/, '$180');

                        const swatchEl = btn.querySelector('.product-details__swatch-icon');
                        if (swatchEl) {
                            if (swatchEl.tagName === 'IMG') {
                                swatchEl.src = swatchUrl;
                            } else {
                                const img    = document.createElement('img');
                                img.src      = swatchUrl;
                                img.alt      = btn.dataset.valueLabel || '';
                                img.className = 'product-details__swatch-icon';
                                img.loading  = 'lazy';
                                swatchEl.replaceWith(img);
                            }
                        }
                        btn.dataset.mainImage = fullUrl;
                    }
                }
            }
        });

        // If the active color is no longer visible, click the first visible one
        if (hasFilter && !activeStillVisible && firstVisible) {
            firstVisible.click();
        }
    }

    async handleSubmit(event) {
        event.preventDefault();

        const variantId = this.variantInput?.value;
        const quantity  = parseInt(this.quantityInput?.value || '1', 10);
        if (!variantId) return;

        const submitBtn = event.target.querySelector('.product-details__add-to-cart');
        const origText  = submitBtn?.textContent || '';
        if (submitBtn) {
            submitBtn.textContent = 'Adding…';
            submitBtn.disabled = true;
        }

        // Collect all line item properties (text, font, colour, pattern, etc.)
        const properties = {};
        event.target.querySelectorAll('input[name^="properties["]').forEach(inp => {
            const key = inp.name.match(/\[(.+)\]/)?.[1];
            if (key && inp.value) properties[key] = inp.value;
        });

        // If a pattern add-on is selected, bundle it as a linked second line item.
        // The pattern is a real cart line (so its inventory is decremented), but it's
        // tied to the main product via a shared hidden _bundle_id so the cart can
        // render it nested and remove/sync it together with the main item.
        const patternVariantId = parseInt(this.addonVariantInput?.value || '', 10);
        const items = [];

        if (patternVariantId) {
            const bundleId = 'tt_' + Date.now().toString(36) + Math.random().toString(36).slice(2, 8);
            const patternTitle = (this.addonTitleInput?.value || '').trim();
            const mainTitle = (document.querySelector('.product-details__title')?.textContent || '').trim();

            properties['_bundle_id'] = bundleId;
            properties['_bundle_role'] = 'main';

            items.push({ id: parseInt(variantId, 10), quantity, properties });
            items.push({
                id: patternVariantId,
                quantity,
                properties: {
                    '_bundle_id': bundleId,
                    '_bundle_role': 'pattern',
                    '_bundled_with': mainTitle,
                    'Pattern': patternTitle
                }
            });
        } else {
            items.push({ id: parseInt(variantId, 10), quantity, properties });
        }

        try {
            const res = await fetch('/cart/add.js', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ items })
            });

            if (!res.ok) {
                const err = await res.json().catch(() => ({}));
                throw new Error(err.description || 'Cart error');
            }

            window.location.href = '/cart';
        } catch (err) {
            console.error('[Add to cart] failed:', err);
            if (submitBtn) {
                submitBtn.textContent = origText;
                submitBtn.disabled = false;
            }
            alert('Sorry, something went wrong. Please try again.');
        }
    }
}

document.addEventListener('DOMContentLoaded', () => {
    handleCardMessage();
    const productDetails = new ProductDetails();
    window.__productDetails = productDetails;
    new PatternAddonPicker();

    // Restore hex swatches after pattern add-on may have swapped images
    setTimeout(() => {
        document.querySelectorAll(
            '.product-details__option-value[data-option="color"][data-color-hex], ' +
            '.product-details__option-value[data-option="colour"][data-color-hex]'
        ).forEach(btn => productDetails.applyHexSwatch(btn));

        const defaultColorBtn =
            document.querySelector('.product-details__option-value[data-option="color"].active') ||
            document.querySelector('.product-details__option-value[data-option="colour"].active') ||
            document.querySelector('.product-details__option-value[data-option="color"]:not([disabled])') ||
            document.querySelector('.product-details__option-value[data-option="colour"]:not([disabled])');
        if (defaultColorBtn) defaultColorBtn.click();
    }, 0);

    // Add entrance animations
    const title = document.querySelector('.product-details__title');
    if (title) {
        setTimeout(() => {
            title.classList.add('animate');
        }, 300);
    }
});
